import { useLocation, useNavigate } from 'react-router-dom';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { useLanguage } from './language-context';
import { useSession } from './session-context';
import { NavIcon } from './nav-icon';
import type { NavIconName } from './nav-icon';
import { BusinessSwitcher } from './business-switcher';

// The context bar under the top bar (design M:303-376; CB-17 `context-bar.tsx`).
//
// Business scope (DR-78 — ONE scope rule): this pill is the only switcher. Every list
// screen (Home, Bookings, Services, Offers, Insights) offers "All businesses"
// (session-context scope) and reads every listing the merchant owns; a single-business
// screen (Team, QR, Inbox, My Business) names the active business, and picking one
// anywhere re-scopes the whole dashboard through PATCH /me/active-business
// (business-switcher.tsx). Services and Insights no longer carry pickers of their own.
// Shown when the merchant holds more than one business.
//
// Quick actions (Home only, M:353-369): New booking (the DC73 wizard, opened on
// Bookings by `?new=1`) · Service · Offer · Share link (QR studio). A team member's
// session (API-16) has neither: no business list to switch, and every quick action
// opens an owner page or needs the merchant-only catalog (the walk-in wizard).
const QUICK: readonly { key: MessageKey; icon: NavIconName; to: string; primary?: boolean }[] = [
  { key: 'dc70.qa.booking', icon: 'plus', to: '/bookings?new=1', primary: true },
  { key: 'dc70.qa.service', icon: 'layers', to: '/services' },
  { key: 'dc70.qa.offer', icon: 'tag', to: '/offers' },
  { key: 'dc70.qa.link', icon: 'link', to: '/qr' },
];

export function ContextBar() {
  const { t } = useLanguage();
  const { businesses, isStaff } = useSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === '/';
  const allowAll =
    isHome || pathname === '/bookings' || pathname.startsWith('/bookings/') || pathname === '/offers' || pathname === '/services' || pathname === '/insights';
  const showSwitcher = businesses.length > 1;
  if (isStaff || (!isHome && !showSwitcher)) return null;
  // My Plan is the account's plan (DR-78, DB-93) — no business scope there.
  if (pathname === '/plan') return null;

  return (
    <div className="ctx-bar" role="region" aria-label={t('dc70.ctx.scope')}>
      {showSwitcher && <BusinessSwitcher variant="context" allowAll={allowAll} />}
      {isHome && (
        <div className="ctx-bar__actions" role="group" aria-label={t('dc70.ctx.quick_actions')}>
          <span className="ctx-bar__rule" aria-hidden="true" />
          <div className="ctx-bar__qas">
            {QUICK.map((q) => (
              <button
                key={q.key}
                type="button"
                className={`ctx-bar__qa${q.primary ? ' ctx-bar__qa--primary' : ''}`}
                onClick={() => navigate(q.to)}
              >
                <NavIcon name={q.icon} size={16} strokeWidth={1.75} />
                <span>{t(q.key)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
